'use client';

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import caseStyles from '../CaseStudy/CaseStudy.module.scss';
import styles from './CaseStudyCarousel.module.scss';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

export type CaseStudyCarouselItem = {
  title: string;
  badge: 'averiane' | 'founder';
  teaserLabel?: string;
  teaserAttribution?: string;
  headlineResult?: string;
  teaserDescription?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

export type CaseStudyCarouselProps = {
  sectionEyebrow?: string;
  sectionTitle?: string;
  sectionIntro?: string;
  items?: CaseStudyCarouselItem[];
  lang?: 'fr' | 'en';
};

const renderHighlighted = (text: string) =>
  text
    .split('*')
    .map((part, i) =>
      i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
    );

/**
 * Homepage "real missions" section: renders the case-study teaser cards as a
 * horizontal carousel instead of a stacked column. Card visuals are reused
 * from the CaseStudy teaser styles to stay consistent with the design system.
 */
export function CaseStudyCarousel({
  sectionEyebrow,
  sectionTitle,
  sectionIntro,
  items = [],
  lang = 'fr',
}: CaseStudyCarouselProps) {
  useAOS();

  const isEn = lang === 'en';
  const labels = {
    badgeAveriane: isEn ? 'Averiane mission' : 'Mission Averiane',
    badgeFounder: isEn
      ? 'Founder-led experience before Averiane'
      : "Expérience d'un fondateur",
    prev: isEn ? 'Previous cases' : 'Cas précédents',
    next: isEn ? 'Next cases' : 'Cas suivants',
    ctaFallback: isEn ? 'Read the case study' : 'Lire l’étude de cas',
  };

  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    setCanPrev(track.scrollLeft > 1);
    setCanNext(track.scrollLeft < track.scrollWidth - track.clientWidth - 1);
  }, []);

  useEffect(() => {
    updateArrows();
    const track = trackRef.current;
    if (!track) {
      return;
    }
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows, items.length]);

  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const card = track.querySelector('article');
    const amount = card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.8;
    gaClick('case_study_carousel_nav', { direction: direction > 0 ? 'next' : 'prev' });
    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <section
      className={caseStyles.teaserSection}
      aria-label={sectionTitle?.trim() || 'Case studies'}
    >
      <div className={caseStyles.container}>
        {(sectionEyebrow?.trim() || sectionTitle?.trim() || sectionIntro?.trim()) && (
          <header
            className={caseStyles.teaserSectionHeader}
            data-aos="fade-up"
            data-aos-duration="700"
          >
            {sectionEyebrow?.trim() && <p className={caseStyles.eyebrow}>{sectionEyebrow}</p>}
            {sectionTitle?.trim() && (
              <h2 className={caseStyles.title}>{renderHighlighted(sectionTitle)}</h2>
            )}
            {sectionIntro?.trim() && <p className={caseStyles.sectionIntro}>{sectionIntro}</p>}
          </header>
        )}

        <div className={styles.carousel}>
          <div
            ref={trackRef}
            className={styles.track}
            onScroll={updateArrows}
            role="region"
            aria-roledescription="carousel"
            aria-label={sectionTitle?.trim() || 'Case studies'}
            // Scrollable region: keyboard users need to scroll the track.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
          >
            {items.map((item, index) => {
              const badgeText =
                item.badge === 'averiane' ? labels.badgeAveriane : labels.badgeFounder;
              const badgeClass =
                item.badge === 'averiane' ? caseStyles.badgeAveriane : caseStyles.badgeFounder;
              return (
                <article
                  key={`${item.title}-${index}`}
                  className={`${caseStyles.teaser} ${styles.card}`}
                  data-aos="fade-up"
                  data-aos-duration="700"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} / ${items.length}`}
                >
                  {item.teaserLabel?.trim() && (
                    <p className={caseStyles.teaserLabel}>{item.teaserLabel}</p>
                  )}
                  <p className={`${caseStyles.teaserAttribution} ${badgeClass}`}>
                    {item.teaserAttribution?.trim() || badgeText}
                  </p>
                  <h3 className={caseStyles.teaserTitle}>{renderHighlighted(item.title)}</h3>
                  {item.headlineResult?.trim() && (
                    <p className={caseStyles.teaserHeadline}>{item.headlineResult}</p>
                  )}
                  {item.teaserDescription?.trim() && (
                    <p className={caseStyles.teaserDescription}>{item.teaserDescription}</p>
                  )}
                  {item.ctaHref && (
                    <a
                      href={item.ctaHref}
                      className={caseStyles.teaserCta}
                      onClick={() =>
                        gaClick('case_study_teaser_cta', {
                          title: item.title,
                          href: item.ctaHref,
                        })
                      }
                    >
                      <span>{item.ctaLabel?.trim() || labels.ctaFallback}</span>
                      <ArrowRight size={16} aria-hidden="true" />
                    </a>
                  )}
                </article>
              );
            })}
          </div>

          {items.length > 1 && (
            <div className={styles.nav}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => scrollByCard(-1)}
                disabled={!canPrev}
                aria-label={labels.prev}
              >
                <ArrowLeft size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => scrollByCard(1)}
                disabled={!canNext}
                aria-label={labels.next}
              >
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
